/**
 * useSEO — sets per-route <head> metadata and CLEANS UP on unmount (Story 30.2).
 *
 * The load-bearing correctness requirement: every tag this hook touches is
 * restored to its baseline on release. Meta tags set in a useEffect without
 * cleanup leak across SPA navigations (a documented project gotcha) — two
 * routes' descriptions accumulate, canonicals stack, robots directives fight.
 *
 * Tags are managed through a module-level REFERENCE-COUNTED registry keyed by
 * CSS selector. This matters because the same singleton tag (robots,
 * canonical, og:*) can be owned by more than one live effect at once — e.g.
 * useNoIndex is applied at the _authenticated/_auth LAYOUT so children are
 * always covered, and a nested route could add its own hook. Under React's
 * children-mount-before-parents ordering, a naive create/remove would let the
 * child's unmount delete a tag the parent still needs (the authenticated page
 * would silently lose its noindex guard). Ref-counting removes/restores a tag
 * only when its LAST owner releases it, so nesting and interleaved SPA
 * transitions are both correct.
 */
import { useEffect } from "react";
import {
  DEFAULT_OG_IMAGE,
  NOINDEX_ROBOTS,
  SITE_ORIGIN,
  type SEOConfig,
} from "../seo/seoConfig";

interface ManagedTag {
  el: Element;
  /** Value before the FIRST acquire; null = the element was created by us. */
  baseline: string | null;
  valueAttr: string;
  refs: number;
}

/** selector → managed tag. Module-level so all hook instances share ownership. */
const registry = new Map<string, ManagedTag>();

/**
 * Acquire shared ownership of a <meta>/<link> tag and set its value. Returns
 * a release fn; the tag is restored to baseline (or removed if we created it)
 * only when the last owner releases.
 */
function acquire(
  tag: "meta" | "link",
  selectorAttr: string,
  selectorValue: string,
  valueAttr: string,
  value: string,
): () => void {
  const selector = `${tag}[${selectorAttr}="${selectorValue}"]`;
  let managed = registry.get(selector);
  if (!managed) {
    const existing = document.head.querySelector(selector);
    if (existing) {
      managed = { el: existing, baseline: existing.getAttribute(valueAttr), valueAttr, refs: 0 };
    } else {
      const created = document.createElement(tag);
      created.setAttribute(selectorAttr, selectorValue);
      document.head.appendChild(created);
      managed = { el: created, baseline: null, valueAttr, refs: 0 };
    }
    registry.set(selector, managed);
  }
  managed.refs += 1;
  managed.el.setAttribute(valueAttr, value); // last writer wins while owned
  return () => {
    managed!.refs -= 1;
    if (managed!.refs > 0) return;
    if (managed!.baseline === null) managed!.el.remove();
    else managed!.el.setAttribute(managed!.valueAttr, managed!.baseline);
    registry.delete(selector);
  };
}

/** Ref-counted <title> management (title is a singleton too). */
const TITLE_KEY = "__title__";
function acquireTitle(value: string): () => void {
  let managed = registry.get(TITLE_KEY);
  if (!managed) {
    // Represent the title via a synthetic managed entry (el unused).
    managed = { el: document.documentElement, baseline: document.title, valueAttr: "title", refs: 0 };
    registry.set(TITLE_KEY, managed);
  }
  managed.refs += 1;
  document.title = value;
  return () => {
    managed!.refs -= 1;
    if (managed!.refs > 0) return;
    document.title = managed!.baseline ?? "";
    registry.delete(TITLE_KEY);
  };
}

const metaName = (name: string, value: string) => acquire("meta", "name", name, "content", value);
const metaProp = (prop: string, value: string) => acquire("meta", "property", prop, "content", value);
const linkRel = (rel: string, href: string) => acquire("link", "rel", rel, "href", href);

/**
 * Apply full SEO metadata for a route. Re-runs and cleans up when the config's
 * identifying fields change.
 */
export function useSEO(config: SEOConfig): void {
  const {
    title,
    description,
    path,
    robots = "index, follow",
    ogType = "website",
    image = DEFAULT_OG_IMAGE,
    twitterCard = "summary_large_image",
  } = config;

  useEffect(() => {
    // Prefer the explicit config path (deterministic — strips query/trailing
    // slash noise that window.location.pathname would bleed into canonical).
    const canonical = `${SITE_ORIGIN}${path ?? window.location.pathname}`;
    const releases: Array<() => void> = [
      acquireTitle(title),
      metaName("description", description),
      linkRel("canonical", canonical),
      metaName("robots", robots),
      metaProp("og:title", title),
      metaProp("og:description", description),
      metaProp("og:type", ogType),
      metaProp("og:url", canonical),
      metaProp("og:image", image),
      metaProp("og:site_name", "ProvenLeap"),
      metaName("twitter:card", twitterCard),
      metaName("twitter:title", title),
      metaName("twitter:description", description),
      metaName("twitter:image", image),
    ];
    return () => {
      for (let i = releases.length - 1; i >= 0; i--) releases[i]!();
    };
  }, [title, description, path, robots, ogType, image, twitterCard]);
}

/**
 * Mark the current route non-indexable (authenticated app + auth flows).
 * Sets ONLY the robots meta (ref-counted) so nested/layout usage is safe.
 */
export function useNoIndex(): void {
  useEffect(() => metaName("robots", NOINDEX_ROBOTS), []);
}
